import React, { useState } from "react";
import PageShell from "@/components/PageShell";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { t } from "@/i18n";

const CONTACT_EMAIL = "info@aaronproperties.it";

export default function Contatti() {
  const { lang } = useApp();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState(""); // honeypot — must stay empty
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !email || !message) {
      toast.error(t("contatti_form_required", lang));
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", { name, email, subject, message, website });
      toast.success(t("contatti_form_sent", lang));
      setName(""); setEmail(""); setSubject(""); setMessage(""); setWebsite("");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : t("error", lang));
    } finally { setBusy(false); }
  };

  return (
    <PageShell
      testid="contatti-page"
      eyebrow={t("contatti_eyebrow", lang)}
      title={t("contatti_title", lang)}
      intro={t("contatti_intro", lang)}
    >
      <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
        <div className="space-y-8">
          <div className="border border-[#D8CFBB] bg-[#FFFAF0] p-8 rounded-lg">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#B8965A] mb-4">{t("contatti_direct", lang)}</p>
            <ul className="space-y-5">
              <li className="flex items-start gap-3">
                <Mail className="h-4 w-4 text-[#B8965A] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-[11px] tracking-[0.25em] uppercase text-[#8A7A5E] mb-1">{t("contatti_email", lang)}</p>
                  <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-email" className="text-[#1A1610] hover:text-[#B8965A] transition-colors font-mono text-sm">
                    {CONTACT_EMAIL}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="h-4 w-4 text-[#B8965A] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-[11px] tracking-[0.25em] uppercase text-[#8A7A5E] mb-1">{t("contatti_phone", lang)}</p>
                  <a href="tel:+393202630305" data-testid="contact-phone" className="text-[#1A1610] hover:text-[#B8965A] transition-colors font-mono text-sm">
                    {t("contatti_phone_number", lang)}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-[#B8965A] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-[11px] tracking-[0.25em] uppercase text-[#8A7A5E] mb-1">{t("contatti_office", lang)}</p>
                  <p className="text-sm text-[#4A3F30]">
                    {t("contatti_office_desc_line1", lang)}
                    <br />
                    {t("contatti_office_desc_line2", lang)}
                  </p>
                </div>
              </li>
            </ul>
          </div>

          <div className="text-xs text-[#8A7A5E] leading-relaxed border-s-2 border-[#B8965A]/40 ps-4">
            {t("contatti_gdpr", lang)}
          </div>
        </div>

        <form onSubmit={submit} className="border border-[#D8CFBB] bg-white p-8 sm:p-10 rounded-lg space-y-5" data-testid="contact-form">
          {/* Honeypot — hidden from humans, visible to bots. Kept out of the tab order & a11y tree. */}
          <div aria-hidden="true" className="absolute -left-[10000px] top-auto w-px h-px overflow-hidden">
            <label htmlFor="c-website">Website (leave empty)</label>
            <input
              id="c-website"
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <Label htmlFor="c-name" className="text-[11px] tracking-[0.2em] uppercase text-[#8A7A5E]">{t("contatti_form_name", lang)}</Label>
              <Input id="c-name" data-testid="contact-name" required value={name} onChange={(e) => setName(e.target.value)} className="mt-1 border-[#D8CFBB] bg-[#FAFAF8]" />
            </div>
            <div>
              <Label htmlFor="c-email" className="text-[11px] tracking-[0.2em] uppercase text-[#8A7A5E]">{t("contatti_form_email", lang)}</Label>
              <Input id="c-email" data-testid="contact-form-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 border-[#D8CFBB] bg-[#FAFAF8]" />
            </div>
          </div>
          <div>
            <Label htmlFor="c-subject" className="text-[11px] tracking-[0.2em] uppercase text-[#8A7A5E]">{t("contatti_form_subject", lang)}</Label>
            <Input id="c-subject" data-testid="contact-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={t("contatti_form_subject_ph", lang)} className="mt-1 border-[#D8CFBB] bg-[#FAFAF8]" />
          </div>
          <div>
            <Label htmlFor="c-msg" className="text-[11px] tracking-[0.2em] uppercase text-[#8A7A5E]">{t("contatti_form_message", lang)}</Label>
            <Textarea id="c-msg" data-testid="contact-message" required rows={7} value={message} onChange={(e) => setMessage(e.target.value)} className="mt-1 border-[#D8CFBB] bg-[#FAFAF8]" />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="contact-submit"
            className="bg-[#B8965A] text-white hover:bg-[#a08248] disabled:opacity-60 tracking-[0.25em] uppercase text-xs px-8 h-12 transition-colors duration-200 flex items-center gap-2"
          >
            <Send className="h-4 w-4" /> {busy ? "…" : t("contatti_form_send", lang)}
          </button>
        </form>
      </div>
    </PageShell>
  );
}
